import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import type {
  UpdateUserExperienceInput,
  UserExperienceSelection,
} from 'bundles/unified-common/contexts/UserExperienceContext';
import type {
  DegreeMembershipElement,
  ProgramMembershipEdge,
} from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import { normalizeSearchString } from 'bundles/unified-common/utils/orgSearchQuery';

// -----------------------------------------------------------------------------
// Route detection
// -----------------------------------------------------------------------------

/** Categorizes the current path as consumer, enterprise (org-scoped), or degree. */
export type RouteType = 'consumer' | 'enterprise' | 'degree';

/** Returns the route type based on pathname (organizations/, programs/all, degrees/, or consumer). */
export function getRouteType(pathname: string): RouteType {
  if (pathname.startsWith('/organizations/')) return 'enterprise';
  if (pathname.startsWith('/programs/all')) return 'enterprise';
  if (pathname.startsWith('/degrees/')) return 'degree';
  return 'consumer';
}

/**
 * Consumer routes that have an enterprise equivalent (unified pages).
 * Enterprise users visiting these should be redirected to the org-scoped version.
 * Expand this list as more pages become unified.
 * Includes consumer XDP paths: learn, specializations, professional-certificates, projects, enroll.
 */
const UNIFIED_CONSUMER_ROUTES = [
  '/',
  '/my-learning',
  '/browse',
  '/search',
  '/learn',
  '/specializations',
  '/professional-certificates',
  '/projects',
  '/enroll',
  '/career-academy',
];

/** Returns true if the consumer pathname is a unified page (has enterprise equivalent). */
export function isUnifiedConsumerRoute(pathname: string): boolean {
  return UNIFIED_CONSUMER_ROUTES.some(
    (route) => pathname === route || (route !== '/' && pathname.startsWith(`${route}/`))
  );
}

/** Extracts org slug from /organizations/:orgSlug/... or /programs/all/:orgSlug/... path. */
function getOrgSlugFromPath(pathname: string): string | undefined {
  const orgMatch = pathname.match(/^\/organizations\/([^/]+)/);
  if (orgMatch) return orgMatch[1];
  const programsAllMatch = pathname.match(/^\/programs\/all\/([^/]+)/);
  return programsAllMatch?.[1];
}

/** Extracts degree slug from /degrees/:degreeSlug/... path, or undefined if not degree route. */
function getDegreeSlugFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/degrees\/([^/]+)/);
  return match?.[1];
}

/** Route context: type, slugs when applicable, and unified consumer flag. */
export type RouteContext = {
  routeType: RouteType;
  orgSlug?: string;
  degreeSlug?: string;
  isUnifiedConsumerRoute: boolean;
};

/** Returns route context from pathname. */
export function getRouteContext(pathname: string): RouteContext {
  const routeType = getRouteType(pathname);
  const orgSlug = routeType === 'enterprise' ? getOrgSlugFromPath(pathname) : undefined;
  const degreeSlug = routeType === 'degree' ? getDegreeSlugFromPath(pathname) : undefined;
  const isOnUnifiedConsumer = routeType === 'consumer' && isUnifiedConsumerRoute(pathname);
  return {
    routeType,
    orgSlug,
    degreeSlug,
    isUnifiedConsumerRoute: isOnUnifiedConsumer,
  };
}

// -----------------------------------------------------------------------------
// User experience selection
// -----------------------------------------------------------------------------

/** Normalized user experience selection (enterprise org, degree, consumer, or unset). */
export type SavedExperience =
  | { type: 'enterprise'; orgSlug: string }
  | { type: 'degree'; degreeSlug: string }
  | { type: 'consumer' }
  | null;

/** Maps UserExperienceContext API response to normalized SavedExperience. */
export function getSavedUserExperience(userExperience: UserExperienceSelection): SavedExperience {
  const selection = userExperience;
  const selectionType = selection?.__typename;

  if (selectionType === 'UserExperienceContext_EnterpriseSelection' && selection?.organization?.slug) {
    return { type: 'enterprise', orgSlug: selection.organization.slug };
  }

  if (selectionType === 'UserExperienceContext_DegreeSelection' && selection?.degreeProgram?.slug) {
    return { type: 'degree', degreeSlug: selection.degreeProgram.slug };
  }

  if (selectionType === 'UserExperienceContext_ConsumerSelection') {
    return { type: 'consumer' };
  }

  return null;
}

// -----------------------------------------------------------------------------
// Redirect resolution and execution
// -----------------------------------------------------------------------------

/** Org/degree with id for set selection. */
export type OrgForRedirect = { id: string; slug: string };
export type DegreeForRedirect = { id: string; slug: string };

/** Minimal membership data for redirect logic. Pass from membership context. */
export type MembershipsForRedirect = {
  orgSlugs: string[];
  degreeSlugs: string[];
  orgs?: OrgForRedirect[];
  degrees?: DegreeForRedirect[];
};

/**
 * Transforms raw program and degree membership data into MembershipsForRedirect for redirect logic.
 * Deduplicates orgs and degrees by slug.
 */
export function transformMembershipsForRedirect(
  programMemberships: ProgramMembershipEdge[] | undefined,
  degreeMemberships: DegreeMembershipElement[] | undefined
): MembershipsForRedirect {
  const orgs: OrgForRedirect[] = [];
  for (const e of programMemberships ?? []) {
    const org = e?.node?.program?.naptimeThirdPartyOrganization;
    if (org?.id && org?.slug && !orgs.some((o) => o.slug === org.slug)) {
      orgs.push({ id: org.id, slug: org.slug });
    }
  }
  const degrees: DegreeForRedirect[] = [];
  for (const e of degreeMemberships ?? []) {
    const deg = e?.degree;
    if (deg?.id && deg?.slug && !degrees.some((d) => d.slug === deg.slug)) {
      degrees.push({ id: deg.id, slug: deg.slug });
    }
  }
  return {
    orgSlugs: orgs.map((o) => o.slug),
    degreeSlugs: degrees.map((d) => d.slug),
    orgs,
    degrees,
  };
}

/** Possible actions from redirect/set-selection resolution. Single source of truth for action values. */
export const SelectionAction = {
  REDIRECT: 'redirect',
  SET_SELECTION: 'setSelection',
} as const;

export type SelectionActionType = (typeof SelectionAction)[keyof typeof SelectionAction];

/** Result of redirect/set-selection resolution. */
export type RedirectOrSetSelectionResult =
  | { action: typeof SelectionAction.REDIRECT; url: string }
  | { action: typeof SelectionAction.SET_SELECTION; input: UpdateUserExperienceInput }
  | null;

/** Optional location so redirect URLs are built via buildRedirectUrl (path-specific overrides). */
export type RedirectLocation = { pathname: string; search: string };

/**
 * Returns redirect URL or set-selection action based on saved experience, route, and memberships.
 * When location is provided, redirect URLs are run through buildRedirectUrl so path-specific
 * behavior (e.g. /search → org search, /browse → org browse) is applied.
 *
 * Flow:
 * 1. Enterprise/degree route + no membership → redirect to org/degree/consumer (gate).
 * 2. Enterprise context + consumer route → redirect to org (trust context).
 * 3. Degree context + consumer/enterprise route → redirect to degree home.
 * 4. Consumer context + unified consumer route + has org/degree memberships → redirect to org (or degree).
 * 5. Unset context + unified consumer route + has memberships → redirect to org (or degree).
 * 6. On matching enterprise/degree route with membership → set selection to sync (no redirect).
 */
export function getRedirectOrSetSelectionAction(
  routeContext: RouteContext,
  savedExperience: SavedExperience,
  memberships?: MembershipsForRedirect | null,
  location?: RedirectLocation
): RedirectOrSetSelectionResult {
  const orgSlugs = memberships?.orgSlugs ?? [];
  const degreeSlugs = memberships?.degreeSlugs ?? [];
  const orgs = memberships?.orgs ?? [];
  const degrees = memberships?.degrees ?? [];
  const hasMemberships = orgSlugs.length > 0 || degreeSlugs.length > 0;
  const hasMembershipData = memberships != null;

  const buildUrl = (url: string) => (location ? buildRedirectUrl(url, location.pathname, location.search) : url);

  const hasOrgMembership = (slug: string) => orgSlugs.includes(slug);
  const hasDegreeMembership = (slug: string) => degreeSlugs.includes(slug);
  const getOrgById = (slug: string) => orgs.find((o) => o.slug === slug);
  const getDegreeById = (slug: string) => degrees.find((d) => d.slug === slug);

  // Gate: enterprise route without membership → consumer home or no action (do not redirect to another org)
  if (routeContext.routeType === 'enterprise' && routeContext.orgSlug && hasMembershipData) {
    if (!hasOrgMembership(routeContext.orgSlug)) {
      if (savedExperience?.type === 'consumer') {
        return { action: SelectionAction.REDIRECT, url: buildUrl('/') };
      }
      // Wrong org: stay on page (may show access UI); do not redirect to another org.
      if (orgSlugs.length >= 1) return null;
      return { action: SelectionAction.SET_SELECTION, input: { selection: 'consumer' } };
    }
  }

  if (routeContext.routeType === 'degree' && routeContext.degreeSlug && hasMembershipData) {
    if (!hasDegreeMembership(routeContext.degreeSlug)) {
      // No membership: enterprise → other degree → consumer.
      if (orgSlugs.length >= 1) {
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/organizations/${orgSlugs[0]}`) };
      }
      if (degreeSlugs.length >= 1) {
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/degrees/${degreeSlugs[0]}/home`) };
      }
      if (savedExperience?.type === 'consumer') {
        return { action: SelectionAction.REDIRECT, url: buildUrl('/') };
      }
      return { action: SelectionAction.SET_SELECTION, input: { selection: 'consumer' } };
    }
  }

  // Enterprise context: consumer unified route → redirect to org.
  if (savedExperience?.type === 'enterprise') {
    const orgSlug = savedExperience.orgSlug;
    const shouldRedirectToOrg = routeContext.routeType === 'consumer' && routeContext.isUnifiedConsumerRoute;
    if (shouldRedirectToOrg) {
      return { action: SelectionAction.REDIRECT, url: buildUrl(`/organizations/${orgSlug}`) };
    }
    // On enterprise route for different org: set selection to current org (no redirect).
    if (routeContext.routeType === 'enterprise' && routeContext.orgSlug && routeContext.orgSlug !== orgSlug) {
      const org = getOrgById(routeContext.orgSlug);
      if (org)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'enterprise', organizationId: org.id } };
    }
    if (routeContext.routeType === 'degree') {
      return { action: SelectionAction.REDIRECT, url: buildUrl(`/organizations/${orgSlug}`) };
    }
  }

  // Degree context: consumer or enterprise route → redirect to degree home.
  if (savedExperience?.type === 'degree') {
    const degreeSlug = savedExperience.degreeSlug;
    if (routeContext.routeType === 'consumer' && routeContext.isUnifiedConsumerRoute) {
      return { action: SelectionAction.REDIRECT, url: buildUrl(`/degrees/${degreeSlug}/home`) };
    }
    if (routeContext.routeType === 'enterprise') {
      return { action: SelectionAction.REDIRECT, url: buildUrl(`/degrees/${degreeSlug}/home`) };
    }
    // On degree route for different degree: set selection to current degree (no redirect).
    if (routeContext.routeType === 'degree' && routeContext.degreeSlug && routeContext.degreeSlug !== degreeSlug) {
      const degree = getDegreeById(routeContext.degreeSlug);
      if (degree)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'degree', degreeProgramId: degree.id } };
    }
  }

  // Consumer context: unified consumer route + memberships → redirect to org (or degree).
  if (savedExperience?.type === 'consumer') {
    if (
      routeContext.routeType === 'consumer' &&
      routeContext.isUnifiedConsumerRoute &&
      hasMembershipData &&
      (orgSlugs.length >= 1 || degreeSlugs.length >= 1)
    ) {
      if (orgSlugs.length >= 1)
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/organizations/${orgSlugs[0]}`) };
      if (degreeSlugs.length >= 1)
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/degrees/${degreeSlugs[0]}/home`) };
    }
    // On enterprise/degree route with membership → set selection to sync.
    if (routeContext.routeType === 'enterprise' && routeContext.orgSlug) {
      const org = getOrgById(routeContext.orgSlug);
      if (org)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'enterprise', organizationId: org.id } };
    }
    if (routeContext.routeType === 'degree' && routeContext.degreeSlug) {
      const degree = getDegreeById(routeContext.degreeSlug);
      if (degree)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'degree', degreeProgramId: degree.id } };
    }
  }

  // Unset context: unified consumer + memberships → redirect; else set selection when on org/degree route.
  // When hasMembershipData is false (e.g. load error), do not assume consumer — let enterprise/degree pages load.
  if (!savedExperience) {
    if (routeContext.routeType === 'consumer' && routeContext.isUnifiedConsumerRoute) {
      if (!hasMemberships && hasMembershipData) {
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'consumer' } };
      }
      if (orgSlugs.length >= 1)
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/organizations/${orgSlugs[0]}`) };
      if (degreeSlugs.length >= 1 && orgSlugs.length === 0)
        return { action: SelectionAction.REDIRECT, url: buildUrl(`/degrees/${degreeSlugs[0]}/home`) };
    }
    if (routeContext.routeType === 'enterprise' && routeContext.orgSlug) {
      const org = getOrgById(routeContext.orgSlug);
      if (org)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'enterprise', organizationId: org.id } };
    }
    if (routeContext.routeType === 'degree' && routeContext.degreeSlug) {
      const degree = getDegreeById(routeContext.degreeSlug);
      if (degree)
        return { action: SelectionAction.SET_SELECTION, input: { selection: 'degree', degreeProgramId: degree.id } };
    }
  }

  return null;
}

/**
 * Builds the final redirect URL from the redirect result and current location.
 * Use this whenever you need to resolve a redirect (e.g. from getRedirectOrSetSelectionAction) into
 * the URL to send the user to. Path-specific overrides live here so all redirect behavior is in one place.
 *
 * Supported redirect targets and behavior:
 * - '/' (consumer home): no query appended; clean home URL.
 * - '/degrees/:slug/home': no query appended; clean degree home.
 * - '/organizations/:orgSlug' (org home): path-specific overrides (redirect to org-scoped version of same page):
 *   - /search → /organizations/:orgSlug/search + query
 *   - /browse or /browse/... → /organizations/:orgSlug/browse + same subpath + query
 *   - any other unified consumer route (e.g. /learn/:slug, /specializations/:slug) → /organizations/:orgSlug + pathname + query
 *   - else org home + query.
 * - Other URLs: result URL + current search string.
 */
export function buildRedirectUrl(redirectResultUrl: string, pathname: string, search: string): string {
  const searchPart = normalizeSearchString(search || '');

  // Consumer home or degree home: do not append search (clean target URL)
  const isConsumerOrDegreeHome =
    redirectResultUrl === '/' || (redirectResultUrl.startsWith('/degrees/') && redirectResultUrl.endsWith('/home'));
  if (isConsumerOrDegreeHome) return redirectResultUrl;

  // Org home: path-specific overrides (preserve consumer path on org-scoped equivalent)
  const orgHomeMatch = redirectResultUrl.match(/^\/organizations\/([^/]+)\/?$/);
  if (orgHomeMatch) {
    const orgSlug = orgHomeMatch[1];
    if (pathname === '/search') return `/organizations/${orgSlug}/search${searchPart}`;
    if (pathname === '/browse' || pathname.startsWith('/browse/')) {
      const browseSubpath = pathname.slice('/browse'.length);
      return `/organizations/${orgSlug}/browse${browseSubpath}${searchPart}`;
    }
    // Redirect to org-scoped version of the same page for any other unified consumer route
    if (pathname !== '/' && isUnifiedConsumerRoute(pathname)) {
      return `/organizations/${orgSlug}${pathname}${searchPart}`;
    }
    return redirectResultUrl.replace(/\/?$/, '') + searchPart;
  }

  return redirectResultUrl + searchPart;
}

/** Guard interface to prevent double redirects without mutating function parameters. */
export type RedirectGuard = {
  get: () => boolean;
  set: (value: boolean) => void;
};

/**
 * Redirects to the given URL at most once. Uses the guard to prevent double redirects
 * (e.g. in React Strict Mode or when the effect re-runs before navigation completes).
 */
export function performRedirectOnce(url: string, guard: RedirectGuard): void {
  if (!guard.get()) {
    guard.set(true);
    redirectClientOrServer(url);
  }
}
